
import { useCallback, useEffect, useState } from "react";

/**
 * BeforeInstallPromptEvent — minimal type for the non-standard event Chrome /
 * Edge / Samsung Internet fire when the browser considers the app installable.
 * Safari/Firefox never fire it (iOS requires manual "Add to Home Screen").
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt: () => Promise<void>;
}

const DISMISS_KEY = "horndeliver.pwa-install.dismissed";
const DISMISS_VERSION = 2; // bump when copy changes — re-shows the banner

export interface PwaInstallState {
  /** True when the browser has fired beforeinstallprompt and we hold the ref. */
  canInstall: boolean;
  /** True on iOS Safari (no beforeinstallprompt — must use "Add to Home Screen"). */
  isIos: boolean;
  /** True when the app is running standalone (already installed). */
  isStandalone: boolean;
  /** True when the user has dismissed the banner in this version. */
  dismissed: boolean;
  /** Trigger the native install prompt. Returns true if the user accepted. */
  promptInstall: () => Promise<boolean>;
  /** Hide the banner (persists to localStorage; re-shows on version bump). */
  dismiss: () => void;
}

function detectIos(): boolean {
  if (typeof navigator === "undefined") return false;
  // Match iPhone, iPad, iPod. Also catch iPadOS 13+ which reports as Mac.
  const ua = navigator.userAgent.toLowerCase();
  const isIosDevice = /iphone|ipad|ipod/.test(ua);
  const isIpadOnMac = /macintosh/.test(ua) && typeof (document as unknown as { ontouchstart?: unknown }).ontouchstart !== "undefined";
  return isIosDevice || isIpadOnMac;
}

function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return !!standalone || iosStandalone;
}

/**
 * usePwaInstall — listens for beforeinstallprompt, tracks dismissal, and
 * exposes a promptInstall() that triggers the native install dialog.
 *
 * Dismissal is persisted to localStorage with a version suffix so a copy
 * change can legitimately re-show the banner to users who dismissed v1.
 */
export function usePwaInstall(): PwaInstallState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos] = useState<boolean>(() => detectIos());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => detectStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(`${DISMISS_KEY}.v${DISMISS_VERSION}`) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onBefore = (e: Event) => {
      e.preventDefault(); // suppress the default mini-infobar
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // Re-check standalone on focus (covers "added to home screen" then returned)
    const onFocus = () => setIsStandalone(detectStandalone());
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(`${DISMISS_KEY}.v${DISMISS_VERSION}`, "1");
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === "accepted") {
      setIsStandalone(true);
      return true;
    }
    // "dismissed" outcome — keep our banner hidden for this session at least
    return false;
  }, [deferred]);

  // Banner should only be eligible when:
  //  - the app isn't already standalone, AND
  //  - either beforeinstallprompt has fired (Chromium) OR we're on iOS Safari
  //    (which never fires it but supports manual A2HS), AND
  //  - the user hasn't dismissed this version
  const canInstall = !isStandalone && (!!deferred || isIos) && !dismissed;

  return {
    canInstall,
    isIos,
    isStandalone,
    dismissed,
    promptInstall,
    dismiss,
  };
}
